(function () {
  const SECTION_SELECTOR = '[data-testimonials-section]';
  const RAIL_SELECTOR = '[data-testimonials-rail]';
  const DECK_SELECTOR = '[data-testimonials-deck-slider]';
  const CARD_SELECTOR = '[data-testimonial-card]';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getScrollStep(rail) {
    const firstCard = rail.querySelector(CARD_SELECTOR);
    if (!firstCard) return Math.max(rail.clientWidth * 0.78, 240);

    const styles = window.getComputedStyle(rail);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return Math.max(firstCard.getBoundingClientRect().width + gap, 240);
  }

  function bindRail(root, cleanups) {
    const rail = root.querySelector(RAIL_SELECTOR);
    if (!rail) return null;

    const cards = Array.from(rail.querySelectorAll(CARD_SELECTOR));
    const dots = Array.from(root.querySelectorAll('[data-testimonials-dot]'));
    const prev = root.querySelector('[data-testimonials-prev]');
    const next = root.querySelector('[data-testimonials-next]');
    const loops = rail.hasAttribute('data-testimonials-rail-loop');
    let activeIndex = 0;

    function normalizeIndex(index) {
      if (!cards.length) return 0;
      return (index + cards.length) % cards.length;
    }

    function getNearestIndex() {
      if (!cards.length) return 0;
      const railCenter = rail.scrollLeft + rail.clientWidth / 2;
      return cards.reduce((closestIndex, card, cardIndex) => {
        const closest = cards[closestIndex];
        const closestCenter = closest.offsetLeft + closest.offsetWidth / 2;
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        return Math.abs(cardCenter - railCenter) < Math.abs(closestCenter - railCenter) ? cardIndex : closestIndex;
      }, 0);
    }

    function renderActive(index) {
      activeIndex = normalizeIndex(index);
      cards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === activeIndex));
      dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', Number(dot.dataset.testimonialsDot || dotIndex) === activeIndex));
    }

    function updateButtons() {
      if (!prev || !next) return;
      if (loops) {
        prev.disabled = false;
        next.disabled = false;
        return;
      }

      const maxScroll = Math.max(rail.scrollWidth - rail.clientWidth, 0);
      prev.disabled = rail.scrollLeft <= 4;
      next.disabled = rail.scrollLeft >= maxScroll - 4 || maxScroll <= 0;
    }

    function scrollToIndex(index) {
      if (!cards.length) return;
      const nextIndex = loops ? normalizeIndex(index) : Math.max(0, Math.min(index, cards.length - 1));
      renderActive(nextIndex);
      rail.scrollTo({
        left: cards[nextIndex].offsetLeft,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    function scrollByDirection(direction) {
      if (loops || dots.length) {
        scrollToIndex(activeIndex + direction);
        return;
      }

      rail.scrollBy({
        left: getScrollStep(rail) * direction,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    const onScroll = () => {
      renderActive(getNearestIndex());
      updateButtons();
    };
    const onResize = updateButtons;
    rail.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    cleanups.push(() => rail.removeEventListener('scroll', onScroll));
    cleanups.push(() => window.removeEventListener('resize', onResize));

    if (prev) {
      const onPrev = () => scrollByDirection(-1);
      prev.addEventListener('click', onPrev);
      cleanups.push(() => prev.removeEventListener('click', onPrev));
    }

    if (next) {
      const onNext = () => scrollByDirection(1);
      next.addEventListener('click', onNext);
      cleanups.push(() => next.removeEventListener('click', onNext));
    }

    dots.forEach((dot, dotIndex) => {
      const onDot = () => scrollToIndex(Number(dot.dataset.testimonialsDot || dotIndex));
      dot.addEventListener('click', onDot);
      cleanups.push(() => dot.removeEventListener('click', onDot));
    });

    requestAnimationFrame(() => {
      renderActive(getNearestIndex());
      updateButtons();
    });

    return {
      activateCard(card) {
        const cardIndex = cards.indexOf(card);
        if (cardIndex >= 0) {
          scrollToIndex(cardIndex);
          return;
        }

        card.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    };
  }

  function bindDeck(root, cleanups) {
    const slider = root.querySelector(DECK_SELECTOR);
    if (!slider) return null;

    const cards = Array.from(slider.querySelectorAll('[data-testimonials-deck-card]'));
    const visualRoot = slider.querySelector('.testimonials-deck__visual');
    const primaryFrame = slider.querySelector('[data-testimonials-deck-frame="primary"]');
    const secondaryFrame = slider.querySelector('[data-testimonials-deck-frame="secondary"]');
    const visualSources = new Map(
      Array.from(slider.querySelectorAll('[data-testimonials-deck-visual-source]')).map((source) => [
        Number(source.dataset.testimonialsDeckVisualSource),
        source
      ])
    );
    const prev = slider.querySelector('[data-testimonials-prev]');
    const next = slider.querySelector('[data-testimonials-next]');
    let active = 0;
    let locked = false;

    if (!cards.length) return null;

    function renderCards(activeIndex) {
      cards.forEach((card, originalIndex) => {
        const order = (originalIndex - activeIndex + cards.length) % cards.length;
        card.dataset.order = String(order);
        card.style.setProperty('--deck-order', order);
      });
    }

    function cloneSourceImage(index) {
      const source = visualSources.get(index);
      const image = source ? source.querySelector('img') : null;
      return image ? image.cloneNode(true) : null;
    }

    function setFrameImage(frame, index) {
      if (!frame) return;
      const image = cloneSourceImage(index);
      frame.textContent = '';
      frame.classList.toggle('is-empty', !image);
      frame.dataset.frameIndex = image ? String(index) : '';
      if (image) frame.appendChild(image);
    }

    function renderVisuals(activeIndex) {
      const secondary = cards.length > 1 ? (activeIndex + 1) % cards.length : -1;
      setFrameImage(primaryFrame, activeIndex);
      setFrameImage(secondaryFrame, secondary);
    }

    function getFrameBox(frame) {
      const rootRect = visualRoot.getBoundingClientRect();
      const frameRect = frame.getBoundingClientRect();
      return {
        top: frameRect.top - rootRect.top,
        right: rootRect.right - frameRect.right,
        bottom: rootRect.bottom - frameRect.bottom,
        left: frameRect.left - rootRect.left,
        transform: window.getComputedStyle(frame).transform
      };
    }

    function applyFrameBox(element, box) {
      element.style.inset = `${box.top}px ${box.right}px ${box.bottom}px ${box.left}px`;
      element.style.transform = box.transform === 'none' ? '' : box.transform;
    }

    function animateSecondaryToPrimary(nextActive, afterNext) {
      if (!visualRoot || !primaryFrame || !secondaryFrame || prefersReducedMotion()) {
        renderVisuals(nextActive);
        return 0;
      }

      const secondaryIndex = Number(secondaryFrame.dataset.frameIndex);
      const flyerImage = secondaryIndex === nextActive ? cloneSourceImage(nextActive) : null;
      if (!flyerImage) {
        renderVisuals(nextActive);
        return 0;
      }

      const startBox = getFrameBox(secondaryFrame);
      const endBox = getFrameBox(primaryFrame);
      const flyer = document.createElement('div');
      let cleanupTimer = null;

      flyer.className = 'testimonials-deck__portrait-flyer';
      flyer.appendChild(flyerImage);
      applyFrameBox(flyer, startBox);
      visualRoot.appendChild(flyer);
      setFrameImage(secondaryFrame, afterNext);
      flyer.getBoundingClientRect();

      const cleanup = () => {
        if (cleanupTimer) window.clearTimeout(cleanupTimer);
        setFrameImage(primaryFrame, nextActive);
        flyer.remove();
      };

      flyer.addEventListener('transitionend', cleanup, { once: true });
      cleanupTimer = window.setTimeout(cleanup, 900);
      requestAnimationFrame(() => applyFrameBox(flyer, endBox));
      return 760;
    }

    function render() {
      renderCards(active);
      renderVisuals(active);
    }

    function move(direction) {
      if (locked) return;
      locked = true;
      const leaving = cards[active];
      const nextActive = (active + direction + cards.length) % cards.length;
      const entering = cards[nextActive];
      const afterNext = cards.length > 1 ? (nextActive + 1) % cards.length : -1;
      const visualDuration = direction > 0 ? animateSecondaryToPrimary(nextActive, afterNext) : 0;
      slider.classList.add('is-moving');
      if (leaving) leaving.classList.add('is-leaving');
      if (entering) entering.classList.add('is-entering');

      window.setTimeout(() => {
        if (leaving) leaving.classList.remove('is-leaving');
        if (entering) entering.classList.remove('is-entering');
        active = nextActive;
        renderCards(active);
        if (direction <= 0 || visualDuration === 0) renderVisuals(active);
        slider.classList.remove('is-moving');
        locked = false;
      }, prefersReducedMotion() ? 0 : Math.max(420, visualDuration));
    }

    if (prev) {
      const onPrev = () => move(-1);
      prev.addEventListener('click', onPrev);
      cleanups.push(() => prev.removeEventListener('click', onPrev));
    }

    if (next) {
      const onNext = () => move(1);
      next.addEventListener('click', onNext);
      cleanups.push(() => next.removeEventListener('click', onNext));
    }

    render();
    return {
      activateCard(card) {
        const cardIndex = cards.indexOf(card);
        if (cardIndex < 0) return;
        active = cardIndex;
        render();
      }
    };
  }

  function initTestimonials(root) {
    if (!root || root.dataset.testimonialsBound === 'true') return;

    const cleanups = [];
    const controllers = [
      bindRail(root, cleanups),
      bindDeck(root, cleanups)
    ].filter(Boolean);

    root.__testimonialsActivateCard = (card) => {
      controllers.forEach((controller) => controller.activateCard(card));
    };

    root.__testimonialsCleanup = () => {
      cleanups.forEach((cleanup) => cleanup());
      root.__testimonialsActivateCard = null;
      root.__testimonialsCleanup = null;
      root.dataset.testimonialsBound = 'false';
    };

    root.dataset.testimonialsBound = 'true';
  }

  function destroyTestimonials(root) {
    if (root && typeof root.__testimonialsCleanup === 'function') {
      root.__testimonialsCleanup();
    }
  }

  function initAllTestimonials() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initTestimonials);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllTestimonials, { once: true });
  } else {
    initAllTestimonials();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target && event.target.querySelector(SECTION_SELECTOR);
    if (root) initTestimonials(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target && event.target.querySelector(SECTION_SELECTOR);
    if (root) destroyTestimonials(root);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const card = event.target && event.target.closest && event.target.closest(CARD_SELECTOR);
    if (!card) return;

    const root = card.closest(SECTION_SELECTOR);
    if (root && typeof root.__testimonialsActivateCard === 'function') {
      root.__testimonialsActivateCard(card);
    }
  });
})();
